<template>
    <div class="dw-10 f fv form-checkbox-list" :key="uid">
        <!-- Label -->
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg form-checkbox-list-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <!-- Option list -->
        <label class="f fh fa-c sm-b-s"
            v-for="opt in opts"
            :for="opt.uid"
            :key="opt.uid"
            :class="[uiCursorOnOption && !(opt.disabled || disabled) ? 'c-p' : '']">
            <div class="mead-checkbox-list-item"
                :class="[
                    'el--s-' + size,
                    opt.checked ? 'el--active' : '',
                    opt.disabled || disabled ? 'c-na el--disabled' : 'c-p'
                ]">
                <input type="checkbox"
                    :name="opt.uid"
                    :id="opt.uid"
                    :value="opt.val"
                    :disabled="opt.disabled || disabled"
                    v-model="selected" />
                <span class="el-frm-check" ref="toggle"></span>
            </div>
            <slot name="optionLeft" :data="opt"></slot>
            <VisualIcon class="sm-r-xs" uiSize="m" :name="opt.icon" v-if="opt.icon" />
            <span class="fg sm-l-s te-note" v-if="opt.lbl">{{ opt.lbl | athT }}</span>
            <slot name="optionRight" :data="opt"></slot>
        </label>
        <slot name="after"></slot>
    </div>
</template>

<script>
import { guuid, isEqual, isNeString, isObject, join } from '@aretheyhappy/utils';
import VisualIcon from '../../atoms/visuals/Icon.vue';

export default {
    name: 'mead-form-checkbox-list',
    components: {VisualIcon},
    props: {
        /**
         * Label i18n key rendered above the checkbox list. Only shown when truthy.
         */
        lbl                 : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity           : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc         : {type: Boolean, default: () => true},
        /**
         * Array of currently selected option values (v-model).
         */
        value               : {type: Array, default: () => []},
        /**
         * Disables every checkbox in the list.
         */
        disabled            : {type: Boolean, default: () => false},
        /**
         * Size modifier applied to each checkbox visual (e.g. 's', 'm').
         */
        size                : {type: String, default: () => 'm'},
        /**
         * List of option objects (`{val, lbl, icon}`) rendered as individual checkboxes.
         */
        options             : {type: Array, default: () => []},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional          : {type: Boolean, default: () => false},
        /**
         * Whether to show a pointer cursor over options that aren't disabled.
         */
        uiCursorOnOption    : {type: Boolean, default: () => true},
    },
    data () {
        return {
            uid: guuid(),
            selected: this.value,
        };
    },
    watch: {
        value: function (new_val, old_val) {
            if (isEqual(new_val, old_val) || isEqual(this.selected, new_val)) return;
            this.selected = this.value;
        },
        selected: function () {
            this.$emit('input', this.selected);
        },
    },
    computed: {
        opts : function () {
            return this.options.reduce((acc, el) => {
                if (
                    !isObject(el) ||
                    !el.hasOwnProperty('val') ||
                    !isNeString(el.lbl)
                ) return acc;

                acc.push(Object.assign(
                    {uid: guuid(), disabled: false, checked: this.selected.includes(el.val)},
                    el
                ));
                return acc;
            }, []);
        },
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/variables';

label {
    &.el--t-listel {
        transition: background-color .2s ease;
        border-radius: $br-s;
        padding: $s-xs;

        &:hover {
            background-color: var(--c-brand-light-bg);
        }
    }
}

.mead-checkbox-list-item {
    cursor: pointer;

    input[type="checkbox"] {
        display: none;
        opacity: 0;
    }

    .el-frm-check {
        display: block;
        width: 2rem;
        height: 2rem;
        line-height: 2rem;
        border: 1px solid var(--c-frm-border);
        background-color: var(--c-frm-panel-bg);
        border-radius: $br-s;
        position: relative;
        cursor: pointer;
        font-weight: 600;
        text-align: center;
        transition: border .15s ease-in-out, color .15s ease-in-out;

        &:after {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: inline-block;
            color: var(--c-txt);
            font-family: 'LinearIcons', sans-serif;
            content: "\e934";
            opacity: 0;
            font-size: $fs-xs;
        }

        &.intermediate:after {
            top: -.1rem;
            left: .1rem;
            content: "\e937";
        }
    }

    & + span {
        cursor: pointer;
    }

    //  MODIFIERS

    &.el--active .el-frm-check,
    input[type="checkbox"]:checked + .el-frm-check {
        background-color: var(--c-brand-active-bg);
        border-color: var(--c-brand-active-bg);

        &:after {
            color: var(--c-brand-active-fg);
            opacity: 1;
        }
    }

    &.el--disabled + span {
        cursor: not-allowed;
    }

    &.el--disabled .el-frm-check,
    input[type="checkbox"]:disabled + .el-frm-check {
        opacity: .65;
        filter: grayscale(1);
        cursor: not-allowed !important;
    }

    //  SIZES

    @mixin elFrmCheckSize ($name, $dim, $br, $fs) {
        &.el--s-#{$name} {
            .el-frm-check {
                width: $dim;
                height: $dim;
                line-height: $dim;
                border-radius: $br;

                &:after {
                    font-size: $fs;
                }
            }
        }
    }

    @include elFrmCheckSize('s', 2rem, $br-s, $fs-xs);
    @include elFrmCheckSize('m', 3rem, $br-m, $fs-l);
}
</style>
